/**
 * Where an AI-assisted ingest writes. Mirrors the Java enum `AiIngestTarget` — these strings are the
 * wire values of `?target=` on the ingest endpoints, so they must match it exactly.
 *
 * Deliberately incomplete relative to the product's grids: there is no target for manual tCO2e entry
 * (EmissionsDataGrid), and only some Scope 3 categories have one. A grid whose lookup returns null gets no
 * AI Mapping button — adding one means a new enum value and a backend handler, not a frontend change.
 */
export const AI_INGEST_TARGETS = {
  SCOPE_1_STATIONARY: "SCOPE_1_STATIONARY",
  SCOPE_1_MOBILE: "SCOPE_1_MOBILE",
  SCOPE_1_REFRIGERANT: "SCOPE_1_REFRIGERANT",
  SCOPE_2_V1_ELECTRICITY: "SCOPE_2_V1_ELECTRICITY",
  SCOPE_2_V2_PURCHASED_ELECTRICITY: "SCOPE_2_V2_PURCHASED_ELECTRICITY",
  SCOPE_2_V2_ONSITE_GENERATION: "SCOPE_2_V2_ONSITE_GENERATION",
  SCOPE_3_BUSINESS_TRAVEL: "SCOPE_3_BUSINESS_TRAVEL",
  SCOPE_3_EMPLOYEE_COMMUTING: "SCOPE_3_EMPLOYEE_COMMUTING",
  SCOPE_3_CAPITAL_GOODS: "SCOPE_3_CAPITAL_GOODS",
  SCOPE_3_PURCHASED_GOODS: "SCOPE_3_PURCHASED_GOODS",
};

// Scope 3, keyed by the CATEGORIES_KEYS value the category components already carry as `tabKey`.
// Only categories with a backend handler appear; the rest resolve to null and render no button.
const TARGET_BY_CATEGORY = {
  businessTravel: AI_INGEST_TARGETS.SCOPE_3_BUSINESS_TRAVEL,
  employeeCommuting: AI_INGEST_TARGETS.SCOPE_3_EMPLOYEE_COMMUTING,
  capitalGoods: AI_INGEST_TARGETS.SCOPE_3_CAPITAL_GOODS,
  // The SPEND sub-section of Cat 1. Its water-usage sub-section has its own table and no target yet, so this
  // key covers only the spend grid — the one that renders CategoryGrid.
  purchasedGoodsAndServices: AI_INGEST_TARGETS.SCOPE_3_PURCHASED_GOODS,
};

// DimensionsGrid's two render sites, keyed by the (scope, sectionType) pair it already receives as props.
// Scope 2 resolves to the v1 target only: the v2 engine has its own component tree (IndirectEmissionsV2),
// and DimensionsGrid is never rendered for it.
const TARGET_BY_SCOPE_AND_SECTION = {
  one: {
    stationary: AI_INGEST_TARGETS.SCOPE_1_STATIONARY,
    mobile: AI_INGEST_TARGETS.SCOPE_1_MOBILE,
    refrigerant: AI_INGEST_TARGETS.SCOPE_1_REFRIGERANT,
  },
  two: {
    electricity: AI_INGEST_TARGETS.SCOPE_2_V1_ELECTRICITY,
  },
};

/**
 * The ingest target for a DimensionsGrid instance, or null when the pair maps to nothing.
 *
 * Returning null rather than throwing is the point: callers use it to decide whether to render the button
 * at all, so an unmapped grid quietly has no button instead of breaking the page.
 */
export function targetForSection(scope, sectionType) {
  if (!scope || !sectionType) return null;
  return TARGET_BY_SCOPE_AND_SECTION[scope]?.[sectionType] ?? null;
}

/**
 * Deep link into AI Data Mapping for one task and destination.
 *
 * `questionnaireId` is the organization questionnaire's id — the same value the task page carries as
 * `?id=`. Both params are required by the target page: without the task it shows an "open this from a data
 * task" state, and without the target the agent has to ask the user which destination it is mapping into.
 *
 * `sessionId` is optional — pass it once a session has been picked or created (e.g. from the "Your
 * sessions" overlay) so the destination page's own bootstrap logic hydrates that session directly instead
 * of showing the picker again.
 *
 * `basePath` is passed in rather than imported from `config/paths` on purpose. That module holds JSX in a
 * `.js` file, so importing it makes this whole module unparseable by Vitest — the same trap documented for
 * `src/utils/index.js`. Callers already import `routes` for other reasons, and this keeps the route itself
 * defined in exactly one place.
 */
export function aiDataMappingHref(basePath, questionnaireId, target, sessionId) {
  const params = new URLSearchParams({
    questionnaireId: String(questionnaireId),
    target,
  });
  if (sessionId) params.set("session", sessionId);
  return `${basePath}?${params.toString()}`;
}

/**
 * The ingest target for a Scope 3 category, or null when that category has no handler yet.
 *
 * Keyed on the CATEGORIES_KEYS value rather than on a display name, because that key is what the category
 * components pass around as `tabKey` and what methodology_map is keyed by on the server.
 */
export function targetForCategory(categoryKey) {
  if (!categoryKey) return null;
  return TARGET_BY_CATEGORY[categoryKey] ?? null;
}
